/**
 * Auth Context Wrapper for authService
 * Provides React context for the existing authService singleton
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(authService.getCurrentUser());
  const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());

  useEffect(() => {
    // Initial check
    setUser(authService.getCurrentUser());
    setIsAuthenticated(authService.isAuthenticated());

    // Listen for auth state changes
    const handleLogout = () => {
      setUser(null);
      setIsAuthenticated(false);
    };

    window.addEventListener('auth:logout', handleLogout);

    // Periodic check for auth state (in case of token refresh)
    const interval = setInterval(() => {
      setUser(authService.getCurrentUser());
      setIsAuthenticated(authService.isAuthenticated());
    }, 1000); // Check every second

    return () => {
      window.removeEventListener('auth:logout', handleLogout);
      clearInterval(interval);
    };
  }, []);

  const value = {
    user,
    isAuthenticated,
    login: (email, password, rememberMe) => authService.login(email, password, rememberMe).then(() => {
      setUser(authService.getCurrentUser());
      setIsAuthenticated(true);
    }),
    logout: async () => {
      await authService.logout();
      setUser(null);
      setIsAuthenticated(false);
    },
    getAccessToken: () => authService.getAccessToken()
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

